import { type FC, type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useClaimFreeDailyDice } from '@/common/hooks/claim-free-dice';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { secondsUntilEndOfDayGMT } from '@/common/lib/secondsUntilEndOfDay';
import { type DicePackageItemType } from '@/common/types/userType';
import { ModalContext } from '@/components/Modals/Modal/config';
import { Glow, ShopBuyButton, ShopTitle } from '@/components/Shop/ui/ShopUi';
import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import lightPng from '/img/shop/v2/card-light.png';
import lightWebp from '/img/shop/v2/card-light.webp';
import dice5Png from '/img/shop/v2/dice/dice-5.png';
import dice5Webp from '/img/shop/v2/dice/dice-5.webp';
import dice15Png from '/img/shop/v2/dice/dice-15.png';
import dice15Webp from '/img/shop/v2/dice/dice-15.webp';
import dice30Png from '/img/shop/v2/dice/dice-30.png';
import dice30Webp from '/img/shop/v2/dice/dice-30.webp';
import dicePng from '/img/shop/v2/dice-icon.png';
import diceWebp from '/img/shop/v2/dice-icon.webp';
import flashPng from '/img/shop/v2/vip/flash.png';
import flashWebp from '/img/shop/v2/vip/flash.webp';

// Paid packages from the smallest one: name and art of the design
const paidTiers: { title: string; art: ReactNode }[] = [
  {
    title: 'Bundle five',
    art: <Picture img={dice5Png} webp={dice5Webp} alt="" className={css.art} />,
  },
  {
    title: 'Lucky set',
    art: (
      <Picture img={dice15Png} webp={dice15Webp} alt="" className={css.art} />
    ),
  },
  {
    title: 'Fortune pack',
    art: (
      <Picture
        img={dice30Png}
        webp={dice30Webp}
        alt=""
        className={cn(css.art, css.art30)}
      />
    ),
  },
];

const titleFor = (pkg: DicePackageItemType, tier: number): string => {
  if (pkg.diceCount === 5) return 'Bundle five';

  if (tier === 0 && pkg.diceCount !== 5) return 'Quick roll';

  return paidTiers[Math.min(tier, paidTiers.length - 1)].title;
};

const oldPriceOf = ({
  amountUsd,
  discountPercent,
}: DicePackageItemType): number =>
  Number((amountUsd / ((100 - discountPercent) / 100)).toFixed(2));

interface DiceCardProps {
  title: string;
  count: number;
  art: ReactNode;
  fullRecharge?: boolean;
  marker?: boolean;
  inactive?: boolean;
  children: ReactNode;
}

const DiceCard: FC<DiceCardProps> = ({
  title,
  count,
  art,
  fullRecharge,
  marker,
  inactive,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className={css.card}>
      <div className={css.bg}>
        <Glow
          color="#5E3B9A"
          blur={32}
          className="w-[174px] h-[102px] left-1/2 -translate-x-1/2 top-1"
        />
        <Glow
          color="#9955FF"
          className="size-40 left-1/2 -translate-x-1/2 -top-[131px]"
        />
      </div>
      <div className={cn(css.media, inactive && 'opacity-50')}>
        <Glow
          color="#5323A0"
          blur={54}
          className="w-[100px] h-[60px] left-1/2 -translate-x-1/2 top-[46px]"
        />
        {!inactive && (
          <Picture
            img={lightPng}
            webp={lightWebp}
            alt=""
            className={css.light}
          />
        )}
        <div className={css.artBox}>{art}</div>
      </div>
      <span className={cn(css.title, inactive && 'opacity-50')}>
        {t(title)}
      </span>
      <div className={cn(css.value, inactive && 'opacity-50')}>
        <span className={css.count}>{count}</span>
        {fullRecharge && (
          <span className="flex items-center gap-0.5">
            <span className={css.recharge}>{t('+Full Recharge')}</span>
            <span className="relative size-4 overflow-hidden">
              <Picture
                img={flashPng}
                webp={flashWebp}
                alt=""
                className="block size-full scale-[1.2522]"
              />
            </span>
          </span>
        )}
      </div>
      <div className={css.button}>{children}</div>
      {marker && <div className={css.marker} />}
    </div>
  );
};

/** "Dice" tab of the shop: today's free dice and the dice packages. */
const DiceMarket: FC = () => {
  const { t } = useTranslation();
  const { openModal } = useContext(ModalContext);
  const packages = useAppSelector(
    (state) => state.user.extendedInfo?.dicePackages,
  );
  const { claim, isClaiming } = useClaimFreeDailyDice();
  const secondsToNewDay = useCountDownTimer(secondsUntilEndOfDayGMT());

  if (!packages?.length) return null;

  // Keep the index in dicePackages: the buy window works with it
  const indexed = packages.map((pkg, index) => ({ pkg, index }));
  const free = indexed.find(({ pkg }) => pkg.free !== undefined);
  const paid = indexed
    .filter(({ pkg }) => pkg.free === undefined)
    .sort((a, b) => a.pkg.diceCount - b.pkg.diceCount);

  const buy = (offerIndex: number): void => {
    openModal({ renderKind: 'buyDiceModal', props: { offerIndex } });
  };

  const hours = Math.floor(secondsToNewDay / 3600);
  const minutes = Math.floor((secondsToNewDay % 3600) / 60);

  return (
    <div className="w-full flex flex-col items-center gap-4 px-4 pt-4 pb-6">
      <ShopTitle>{t('More dice, more wins!')}</ShopTitle>
      <div className="grid grid-cols-2 gap-4 w-full">
        {free && (
          <DiceCard
            title="Daily luck"
            count={free.pkg.diceCount}
            marker={!!free.pkg.free}
            inactive={!free.pkg.free}
            art={
              <div className={css.freeDice}>
                <Picture
                  img={dicePng}
                  webp={diceWebp}
                  alt=""
                  className="block size-full"
                />
                <div className={css.freeDiceTint} />
              </div>
            }
          >
            {free.pkg.free ? (
              <ShopBuyButton
                label={t('Free')}
                tag={t('DAILY')}
                isLoading={isClaiming}
                disabled={isClaiming}
                onClick={() => void claim({ silentStatus: true })}
              />
            ) : (
              <ShopBuyButton
                variant="locked"
                label={`${hours}h : ${String(minutes).padStart(2, '0')}m`}
                disabled
              />
            )}
          </DiceCard>
        )}
        {paid.map(({ pkg, index }, tier) => (
          <DiceCard
            key={pkg.diceCount}
            title={titleFor(pkg, tier)}
            count={pkg.diceCount}
            fullRecharge={pkg.restoreEnergy > 0}
            art={paidTiers[Math.min(tier, paidTiers.length - 1)].art}
          >
            <ShopBuyButton
              usd={pkg.amountUsd}
              oldUsd={pkg.discountPercent ? oldPriceOf(pkg) : undefined}
              discount={pkg.discountPercent || undefined}
              onClick={() => buy(index)}
            />
          </DiceCard>
        ))}
      </div>
    </div>
  );
};

export { DiceMarket };
